'use client';

import { useRef } from 'react';
import { useIntl } from 'react-intl';
import PageType from '@alltrails/amplitude/enums/PageType';
import logPhotoCarouselSwiped from '@alltrails/amplitude/events/logPhotoCarouselSwiped';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import getPhotoUrlFromId from '@alltrails/core/utils/getPhotoUrlFromId';
import SwiperCarousel from '@alltrails/denali/components/SwiperCarousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import PhotoSlash from '@alltrails/denali/icons/PhotoSlash';
import { PhotoHash } from '@/types/shared';
import getLocationPhotoTitle from '@/utils/getLocationPhotoTitle';
import { useLightbox } from '../Lightbox/LightboxContext';
import PhotoHeaderGrid from '../PhotoHeaderGrid/PhotoHeaderGrid';
import LocationPhotoCard from './LocationPhotoCard';
import styles from './PhotoHeader.module.scss';

type Props = {
  photos: PhotoHash[];
  locationDetails: any;
  id?: number;
  pageType?: PageType;
};

const PhotoHeader = ({ photos, locationDetails, id, pageType }: Props) => {
  const intl = useIntl();
  const { openLightbox } = useLightbox();
  const swiper = useRef(false);
  const { type, locationSlug, name, city, state, country } = locationDetails;
  const getImageUrl = (id: number, photoHash: string) => {
    if (id && photoHash) {
      return getPhotoUrl({ isOptimized: true, photo: { id, photoHash }, size: '1080x700' });
    }
    return getPhotoUrlFromId({ id, size: '500x500' });
  };
  const isMobile = useIsMobileSizedScreen();
  const shouldRenderPhotoGrid = pageType !== 'poi page' && !isMobile && photos.length > 2;
  if (photos.length === 0 && !isMobile) {
    return <div className={styles.emptyPhoto} />;
  }

  return (
    <div className={styles.photoHeaderContainer}>
      {shouldRenderPhotoGrid ? (
        <PhotoHeaderGrid photos={photos} locationDetails={locationDetails} pageType={pageType} />
      ) : photos.length > 0 ? (
        <SwiperCarousel
          className={styles.carouselContainer}
          uniqueId="locations-photos-carousel"
          items={photos}
          hasPaginationGradient
          renderItem={(photo, photoIndex) => (
            <LocationPhotoCard
              photoCount={photos.length}
              photoId={(photo as PhotoHash).id}
              key={(photo as PhotoHash).id}
              index={photoIndex}
              url={getImageUrl((photo as PhotoHash).id, (photo as PhotoHash).photoHash)}
              openLightbox={openLightbox}
              locationSlug={locationSlug}
              locationDetails={{ type, city, name, state, country }}
              pageType={pageType}
              photoTitle={getLocationPhotoTitle(photo as PhotoHash, locationDetails, intl)}
            />
          )}
          getItemKey={(photo: any) => String(photo.id)}
          showPaginationDots={photos.length > 1}
          hideSlideOverflow
          arrowStyle="subtle-with-standard-arrows"
          scrollMode={photos.length < 2}
          onSlideChange={() => {
            if (!swiper.current && pageType && id) {
              logPhotoCarouselSwiped({ carousel_item_id: String(id), page_type: pageType, photo_carousel_type: 'hero image' });
              swiper.current = true;
            }
          }}
        />
      ) : (
        <div className={styles.noImage}>
          <PhotoSlash color="--color-text-secondary" size="lg" />
        </div>
      )}
    </div>
  );
};

export default PhotoHeader;
